import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Sparkles, Stethoscope, ShieldCheck,
  Phone, Mail, ChevronRight,
} from "lucide-react";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import PageMeta from "../../components/seo/PageMeta";
import CeipalJobsWidget from "../../components/CeipalJobsWidget";
import {
  CEIPAL_SUBMIT_RESUME_URL,
  RECRUITER_EMAIL,
  RECRUITER_PHONE,
  RECRUITER_PHONE_HUMAN,
} from "../../data/recruitingLinks";

/* ------------------------------------------------------------------ */
/*  /careers/jobs                                                      */
/*                                                                    */
/*  Embeds the CEIPAL widget — the ONLY page that does. ShiftBridge   */
/*  PRN marketplace pages are deliberately kept widget-free.           */
/* ------------------------------------------------------------------ */

const SITE   = "https://www.tritalcare.com";
const URL    = `${SITE}/careers/jobs`;
const OG_IMG = `${SITE}/images/logos/trital-shiftbridge-logo-transparent-master.png`;

export default function CareersJobs() {
  return (
    <div className="min-h-screen bg-white" data-testid="careers-jobs-page">
      <PageMeta
        title="Healthcare Jobs, Nursing Jobs & Allied Health Careers | TRITAL Care®"
        description="Search open healthcare jobs with TRITAL Care, including nursing, allied health, therapy, travel, local, contract, and permanent opportunities."
        canonical={URL}
        ogImage={OG_IMG}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home",     "item": `${SITE}/` },
              { "@type": "ListItem", "position": 2, "name": "Careers",  "item": `${SITE}/careers` },
              { "@type": "ListItem", "position": 3, "name": "Healthcare Jobs", "item": URL },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": ["EmploymentAgency", "MedicalBusiness"],
            "name": "TRITAL Care® — Healthcare Jobs",
            "url": URL,
            "logo": OG_IMG,
            "description": "Open healthcare jobs at TRITAL Care: nursing, allied health, therapy, travel, local, contract and permanent placements nationwide.",
            "parentOrganization": { "@type": "Organization", "name": "TRITAL Care®", "url": SITE },
            "areaServed": { "@type": "Country", "name": "United States" },
            "telephone": "+1-832-344-1525",
            "email": "careers@tritalcare.com",
          },
        ]}
      />
      <PublicNav />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-trital-mesh border-b border-slate-200" data-testid="careers-jobs-hero">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 pt-14 pb-12 lg:pt-20 lg:pb-16">
          <nav className="flex items-center gap-1.5 text-[12.5px] text-slate-500 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-trital-navy">Home</Link>
            <ChevronRight className="w-3 h-3" strokeWidth={1.8} />
            <Link to="/careers" className="hover:text-trital-navy">Careers</Link>
            <ChevronRight className="w-3 h-3" strokeWidth={1.8} />
            <span className="text-trital-navy font-semibold">Healthcare Jobs</span>
          </nav>

          <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{duration:0.4}} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
              <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
              <span className="overline text-trital-navy">Healthcare Jobs · Live openings</span>
            </div>
            <h1 className="mt-6 font-display font-black text-5xl lg:text-[60px] leading-[1.02] tracking-tight text-slate-900 text-balance" data-testid="careers-jobs-h1">
              Healthcare Jobs with TRITAL Care<sup>®</sup>
            </h1>
            <p className="mt-5 text-slate-700 max-w-2xl text-lg leading-relaxed">
              Open positions for <strong>nurses, allied health professionals, therapists, and physicians</strong> — travel, local, contract, and permanent placements at vetted health systems nationwide. Transparent rates, full credentialing support, and a recruiter who actually picks up the phone.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href={CEIPAL_SUBMIT_RESUME_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-red text-white text-[13.5px] font-semibold btn-lift hover:bg-trital-red-deep"
                data-testid="careers-jobs-submit-resume"
              >
                Submit Your Resume <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
              </a>
              <a
                href={`sms:${RECRUITER_PHONE}`}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-navy text-white text-[13.5px] font-semibold btn-lift hover:bg-trital-navy-deep"
                data-testid="careers-jobs-text-recruiter"
              >
                <Phone className="w-4 h-4" strokeWidth={1.8} /> Text a Recruiter
              </a>
              <a
                href={`mailto:${RECRUITER_EMAIL}`}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 text-trital-navy text-[13.5px] font-semibold hover:border-trital-navy"
                data-testid="careers-jobs-email-recruiter"
              >
                <Mail className="w-4 h-4" strokeWidth={1.8} /> Email Recruiting
              </a>
            </div>

            <div className="mt-6 text-[12.5px] text-slate-500">
              Recruiter Support: <a className="text-trital-navy font-semibold" href={`tel:${RECRUITER_PHONE}`}>{RECRUITER_PHONE_HUMAN}</a> · <a className="text-trital-navy font-semibold" href={`mailto:${RECRUITER_EMAIL}`}>{RECRUITER_EMAIL}</a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ CEIPAL WIDGET ============ */}
      <section className="bg-white" data-testid="careers-jobs-widget-section">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12 lg:py-16">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="overline text-trital-red">Current openings</div>
              <h2 className="mt-2 font-display font-black text-2xl lg:text-3xl text-slate-900">
                Live healthcare jobs — filter, click, apply.
              </h2>
            </div>
            <a
              href={CEIPAL_SUBMIT_RESUME_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-trital-navy text-[13px] font-semibold underline underline-offset-2 hover:text-trital-red inline-flex items-center gap-1"
              data-testid="careers-jobs-widget-submit-resume"
            >
              Don't see your role? Submit your resume <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
            </a>
          </div>
          <CeipalJobsWidget />
        </div>
      </section>

      {/* ============ TRUST BAR ============ */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="careers-jobs-trust">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { Icon: ShieldCheck, t: "Full credentialing support", d: "Joint-Commission-aligned compliance team handles your paperwork end-to-end." },
            { Icon: Stethoscope, t: "Clinician-first recruiting",  d: "Recruiters are paired by specialty — your point of contact has placed your role before." },
            { Icon: Sparkles,    t: "Transparent W-2 pay",           d: "Posted rate is the rate. Weekly direct deposit. No surprise 1099." },
            { Icon: ArrowRight,  t: "Local · Travel · Contract · Perm", d: "Whatever fits your career stage — we have placements across all four tracks." },
          ].map((b, i) => (
            <div key={b.t} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card" data-testid={`careers-jobs-trust-${i}`}>
              <div className="w-10 h-10 rounded-lg bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center">
                <b.Icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div className="mt-3 font-display font-bold text-[15px] text-slate-900">{b.t}</div>
              <div className="mt-1 text-[13px] text-slate-600 leading-relaxed">{b.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="bg-trital-navy-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-90 pointer-events-none" />
        <div className="absolute inset-0 dot-grid opacity-10 pointer-events-none" />
        <div className="relative max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-20 text-center">
          <h2 className="font-display font-black text-3xl lg:text-5xl leading-[1.05] text-white text-balance">
            Looking for <span className="text-trital-red">PRN shifts</span> instead?
          </h2>
          <p className="mt-4 text-white/80 max-w-2xl mx-auto">
            TRITAL ShiftBridge™ is our W-2 PRN shift marketplace — open shifts at vetted hospitals & SNFs nationwide. Weekly pay, zero clinician fees.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/shiftbridge"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-trital-red text-white font-semibold btn-lift hover:bg-trital-red-deep"
              data-testid="careers-jobs-shiftbridge"
            >
              Visit ShiftBridge — PRN Marketplace <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
